import "../types/index.js";

import { createElements } from "../utilities/elements.js";
import { NumberObject } from "../utilities/number_object.js";
import { ApiService } from "./ApiService.js";

export class PostApiService extends ApiService {
  /**
   * @param {string} token pagination token
   * @param {string} format "thumbnail" or "feeditem"
   */
  getPosts(token, format) {
    return this.fetchPosts(`/api/posts?token=${token}&format=${format}`);
  }

  /**
   * @param {string} feed
   */
  getFeed(feed) {
    return this.fetchPosts(`/api/feeds/${feed}`);
  }

  /**
   * @param {string} query
   * @param {string} format "thumbnail" or "feeditem"
   */
  searchPosts(query, format) {
    return this.fetchPosts(`/api/posts/search?query=${query}&format=${format}`);
  }

  /**
   * @param {string} postId
   * @param {number} posterId
   * @throws {ApiError}
   * @returns {Promise<boolean>}
   */
  async likePost(postId, posterId) {
    const response = await this.post(
      `/api/posts/${postId}/like?posterId=${posterId}`,
    );
    if (response.ok) {
      return true;
    } else {
      throw this.buildApiError(response);
    }
  }

  /**
   * @param {string} postId
   * @param {number} posterId
   * @throws {ApiError}
   * @returns {Promise<boolean>}
   */
  async unlikePost(postId, posterId) {
    const response = await this.delete(
      `/api/posts/${postId}/like?posterId=${posterId}`,
    );
    if (response.ok) {
      return true;
    } else {
      throw this.buildApiError(response);
    }
  }

  /**
   * @param {string} postId
   * @param {number} posterId
   * @throws {ApiError}
   * @returns {Promise<boolean>}
   */
  async savePost(postId, posterId) {
    const response = await this.post(
      `/api/posts/${postId}/save?posterId=${posterId}`,
    );
    if (response.ok) {
      return true;
    } else {
      throw this.buildApiError(response);
    }
  }

  /**
   * @param {string} postId
   * @param {number} posterId
   * @throws {ApiError}
   * @returns {Promise<boolean>}
   */
  async unsavePost(postId, posterId) {
    const response = await this.delete(
      `/api/posts/${postId}/save?posterId=${posterId}`,
    );
    if (response.ok) {
      return true;
    } else {
      throw this.buildApiError(response);
    }
  }

  /**
   * @param {string} postId
   * @param {string} reason
   * @param {string} details
   * @throws {ApiError}
   * @returns {Promise<Boolean>}
   */
  async reportPost(postId, reason, details) {
    reason = encodeURIComponent(reason);
    details = encodeURIComponent(details);
    const response = await this.post(
      `/api/posts/${postId}/report?reason=${reason}&details=${details}`,
    );
    if (response.ok) {
      return true;
    } else {
      throw this.buildApiError(response);
    }
  }

  /**
   * @private
   * @param {string} url
   * @throws {ApiError}
   * @returns {Promise<{prev: ?string, next: ?string, items: HTMLCollection}>}
   */
  async fetchPosts(url) {
    const response = await this.get(url, { redirect: "manual" });
    const status = new NumberObject(response.status);
    if (status.equals(200)) {
      return {
        prev: response.headers.get("X-Prev"),
        next: response.headers.get("X-Next"),
        items: createElements(await response.text()),
      };
    } else if (status.inRange(300, 399) && response.headers.has("Location")) {
      window.location.href = response.headers.get("Location");
    } else {
      throw this.buildApiError(
        response,
        `Failed to fetch posts: ${response.statusText} (${response.status})`,
      );
    }
  }
}
